import axios from 'axios'
import { BASE_URL } from '@/environment/environment'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode'

function unwrapList<T>(data: T[] | { results?: T[] } | null | undefined): T[] {
  if (Array.isArray(data))
    return data
  if (Array.isArray(data?.results))
    return data.results
  return []
}

function mapNamedTree(nodes: Array<{ id: string | number, name: string, children?: Array<{ id: string | number, name: string, children?: unknown[] }> }>): ITreeNode[] {
  return nodes.map(node => ({
    key: String(node.id),
    label: node.name,
    data: {
      name: node.name,
      selfId: String(node.id),
    },
    children: node.children?.length ? mapNamedTree(node.children as typeof nodes) : [],
  }))
}

export function omitTreeIds(nodes: ITreeNode[], banned: Set<string>): ITreeNode[] {
  return nodes
    .filter(node => !banned.has(String(node.key)) && !banned.has(String(node.data?.selfId ?? '')))
    .map(node => ({
      ...node,
      children: omitTreeIds(node.children || [], banned),
    }))
}

export async function searchHospitalTree(query: string): Promise<ITreeNode[]> {
  const response = await axios.get(`${BASE_URL}/api/hospital/hospital/`, {
    params: { search: query },
  })
  return useApiStore().getTree(unwrapList(response.data))
}

export async function searchDepartments(query: string): Promise<ITreeNode[]> {
  const response = await axios.get(`${BASE_URL}/api/employee/department/`, {
    params: { search: query },
  })
  return useApiStore().getTree(unwrapList(response.data))
}

export async function searchManagementDepartments(query: string): Promise<ITreeNode[]> {
  const response = await axios.get(`${BASE_URL}/api/employee/management/department/`, {
    params: { search: query },
  })
  return mapNamedTree(unwrapList(response.data))
}

export async function fetchTicketCategories(params: Record<string, unknown>): Promise<any[]> {
  const response = await axios.get(`${BASE_URL}/api/ticket/category/`, { params })
  return unwrapList(response.data)
}

export async function searchTicketServices(query: string): Promise<any[]> {
  const response = await axios.get(`${BASE_URL}/api/ticket/service/`, {
    params: { search: query },
  })
  return unwrapList(response.data)
}

export async function searchAdverseEventTypes(query: string): Promise<ITreeNode[]> {
  const response = await axios.get(`${BASE_URL}/api/adverse/adverse-event-type/tree-nodes/`, {
    params: { search: query },
  })
  return mapNamedTree(unwrapList(response.data))
}
